import { Check, Lock, Package, Plus, Warehouse } from 'lucide-react';
import {
  type StockGroupDto,
} from '@eombor/contracts';
import { Badge, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { warehousesOf, type WarehouseStock } from '../model/stock-warehouses.js';

/**
 * MAHSULOT KARTOCHKASI — BUYURTMACHINING ko'rinishi.
 *
 * `StockResultRow` dan farqi bitta savolda. Skladchining savoli "qaysi
 * katakdagi qaysi partiyada?"; buyurtmachiniki esa "bormi, qancha va
 * qaysi ombordan olaman?". Ikkinchi savolga partiya raqami, band miqdori
 * va harakat tarixi javob bermaydi — ular shunchaki ekranni to'ldirib,
 * asosiy sonni ko'rinmas qilib qo'yadi.
 *
 * Shuning uchun bu yerda MAVJUD SON markazda turadi: u eng katta shrift,
 * ostida esa uni tushuntiradigan ikkilamchi matn. Katak kodi, partiya
 * raqami va tarix umuman chizilmaydi.
 *
 * OMBOR RO'YXATI esa — `onOrder` berilganda — matn emas, TANLOV bo'ladi.
 * Ilgari kartochka ostida bitta umumiy "Savatga qo'shish" tugmasi turardi
 * va u aytilmagan savolga o'zi javob berardi: qaysi ombordan? Mahsulot ikki
 * omborda bo'lsa, tanlovni odam emas, kod qilardi.
 */

/** Miqdor `numeric(14,3)` bilan bir xil yumaloqlanadi — hisob bu yerda emas. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** Ekranda ko'rsatiladigan omborlar soni — qolgani sanoq bo'lib qisqaradi. */
const SHOWN_WAREHOUSES = 4;

export interface StockProductCardProps {
  group: StockGroupDto;
  /** Kam qoldiq chegarasi — katalogdan. Ma'lum bo'lmasa belgi chizilmaydi. */
  minStock?: number | null;
  /**
   * Savatga qo'shish. Berilmasa ombor ro'yxati oddiy MATN bo'lib qoladi —
   * qoldiq ko'rish ekranida bosiladigan narsa yo'q.
   */
  onOrder?: (group: StockGroupDto, warehouse: WarehouseStock) => void;
  /** Savatda TURGAN omborlar — shu mahsulot uchun. */
  orderedWarehouseIds?: ReadonlySet<string>;
  /**
   * Talabnoma yozish RUXSATI bor omborlar.
   *
   * Berilsa, boshqa ombor ro'yxatdan yo'qolmaydi — ko'rinadi, lekin
   * bosilmaydi va NEGA bosilmasligi yoziladi. Jimgina yashirish "bu yerda
   * yo'q" degan yolg'on javobni berardi.
   */
  allowedWarehouseIds?: ReadonlySet<string>;
  /**
   * KUTILAYOTGAN talab — ombor bo'yicha (`warehouseId` → miqdor).
   *
   * Band QILINMAGAN, lekin boshqa talabnomalarda so'ralgan miqdor. Usiz
   * ekran "hammasi bor" deb turaveradi va uch stansiya bir vaqtda o'sha
   * bitta partiyani so'rab qo'yishi mumkin — yo'qlikni esa faqat skladchi
   * topadi.
   */
  pendingOf?: (warehouseId: string) => number;
  /** Ro'yxatdagi o'rin — kartochkalar ketma-ket chiqishi uchun. */
  index?: number;
}

export const StockProductCard = ({
  group,
  minStock = null,
  onOrder,
  orderedWarehouseIds,
  allowedWarehouseIds,
  pendingOf,
  index = 0,
}: StockProductCardProps) => {
  const low = minStock !== null && group.totalAvailableQty < minStock;
  const none = group.totalAvailableQty <= 0;
  const warehouses = warehousesOf(group);
  const reserved = Math.max(0, group.totalQty - group.totalAvailableQty);

  /* Chiziq MAVJUD ulushini ko'rsatadi. Band qilingan miqdor sonda ham
     yozilgan, lekin nisbatni ko'z chiziqdan tezroq oladi. */
  const availableShare =
    group.totalQty <= 0 ? 0 : Math.round((group.totalAvailableQty / group.totalQty) * 100);

  return (
    <li
      className={cn(
        'motion-slide-in flex flex-col gap-4 rounded-[var(--radius-lg)] border p-4',
        'border-border bg-surface shadow-[var(--shadow-1)]',
        'transition-[border-color,box-shadow] duration-[var(--motion-base)]',
        'ease-[var(--ease-standard)] hover:shadow-[var(--shadow-2)]',
      )}
      /* Ketma-ket chiqish — qidiruv natijasi bir zumda "otilib" tushmaydi.
         Kechikish `prefers-reduced-motion` da nolga tushadi (`tokens.css`). */
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
    >
      <header className="flex min-w-0 items-start gap-3">
        {/* BELGI — kartochka nima haqidaligini o'qishdan oldin aytadi va
            ro'yxatdagi kartochkalarga bir xil boshlanish nuqtasi beradi. */}
        <span
          aria-hidden
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]',
            none ? 'bg-surface-2 text-text-faint' : 'bg-accent-soft text-accent',
          )}
        >
          <Package size={19} />
        </span>
        <span className="flex min-w-0 flex-col gap-1">
          <h2 className="min-w-0 text-base leading-snug font-semibold text-balance text-text">
            {group.name}
          </h2>
          <span className="flex flex-wrap items-center gap-2">
            <span className="tabular text-xs text-text-faint">{group.code}</span>
            {low && !none ? <Badge tone="warn">{t('stock.lowStock')}</Badge> : null}
            {none ? <Badge tone="neutral">{t('stock.outOfStock')}</Badge> : null}
          </span>
        </span>
      </header>

      {/* MAVJUD SON — kartochkaning markazi. Buyurtmachi bir qarashda shu
          raqamni izlaydi, qolgani uni tushuntiradi. */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              'tabular text-3xl leading-none font-semibold',
              none ? 'text-text-faint' : 'text-text',
            )}
          >
            {qtyText(group.totalAvailableQty)}
          </span>
          <span className="text-sm text-text-muted">{unitText(group.unit)}</span>
          <span className="ml-auto text-xs text-text-faint">{t('stock.totalAvailable')}</span>
        </div>

        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2"
          role="progressbar"
          aria-valuenow={availableShare}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('stock.totalAvailable')}
        >
          {/* To'lgan qism — MAVJUD ulush. Ilgari u `--color-fill-empty`
              (och kulrang) bilan chizilardi va "bo'sh" degan ma'noni
              berardi: chiziq mavjud qoldiqni ko'rsatsa-yu, rangi bo'shliqni
              aytsa, ko'z ikkisini teskari o'qiydi. */}
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-[var(--motion-base)]',
              'ease-[var(--ease-standard)]',
              none ? 'bg-transparent' : low ? 'bg-warn' : 'bg-accent',
            )}
            style={{ width: `${availableShare}%` }}
          />
        </div>

        {reserved > 0 ? (
          <span className="tabular text-xs text-text-faint">
            {t('stock.reservedOf', {
              reserved: qtyText(reserved),
              total: qtyText(group.totalQty),
              unit: unitText(group.unit),
            })}
          </span>
        ) : null}
      </div>

      {/* QAYERDAN OLASIZ — katak kodi yo'q: buyurtmachi mahsulotni o'zi olib
          kelmaydi, uni skladchi beradi. Ombor esa kerak, chunki talabnoma
          AYNAN bitta omborga yoziladi. */}
      {warehouses.length === 0 ? null : (
        <div className="flex flex-col gap-2">
          <span className="flex items-center gap-1.5 text-[0.6875rem] font-semibold tracking-wide text-text-faint uppercase">
            <Warehouse size={12} aria-hidden />
            {t(onOrder === undefined ? 'stock.inWarehouses' : 'requisition.pickWarehouse')}
          </span>
          <ul className="flex flex-col gap-1.5">
            {warehouses.slice(0, SHOWN_WAREHOUSES).map((row) => (
              <WarehouseRow
                key={row.warehouseId}
                row={row}
                unit={group.unit}
                pending={pendingOf?.(row.warehouseId) ?? 0}
                ordered={orderedWarehouseIds?.has(row.warehouseId) ?? false}
                allowed={allowedWarehouseIds?.has(row.warehouseId) ?? true}
                {...(onOrder === undefined
                  ? {}
                  : { onOrder: () => onOrder(group, row) })}
              />
            ))}
            {warehouses.length > SHOWN_WAREHOUSES ? (
              <li className="px-1 text-xs text-text-faint">
                {t('stock.moreWarehouses', { count: warehouses.length - SHOWN_WAREHOUSES })}
              </li>
            ) : null}
          </ul>
        </div>
      )}
    </li>
  );
};

interface WarehouseRowProps {
  row: WarehouseStock;
  unit: StockGroupDto['unit'];
  /** Band qilinmagan, lekin boshqa talabnomalarda so'ralgan miqdor. */
  pending: number;
  ordered: boolean;
  allowed: boolean;
  onOrder?: () => void;
}

/**
 * Bitta ombor qatori — `onOrder` bo'lmasa MATN, bo'lsa TUGMA.
 *
 * Ikki holat bitta joyda turadi, chunki ular bir xil narsani ko'rsatadi:
 * "shu omborda shuncha bor". Ikki komponent bo'lsa, qoldiq ekranidagi
 * qator bilan talabnoma ekranidagisi asta-sekin bir-biridan uzoqlashardi.
 */
const WarehouseRow = ({ row, unit, pending, ordered, allowed, onOrder }: WarehouseRowProps) => {
  /*
   * ERKIN qoldiq — mavjuddan kutilayotgan talab ayirilgani.
   *
   * "Mavjud" soni yolg'iz turganda yolg'on gapiradi: u faqat QATTIQ
   * bronni hisobga oladi, bron esa skladchi qabul qilgandagina qo'yiladi.
   * Undan oldingi ikki bosqichda so'rov bor, lekin son o'zgarmaydi.
   *
   * Manfiy bo'lishi MUMKIN va u nolga qisqartirilmaydi: "3 ta yetmayapti"
   * degani va uni yashirish aynan qaror qabul qiladigan odamdan
   * yashirish bo'lardi.
   */
  const free = row.availableQty - pending;
  const qty = (
    <>
      <span className="tabular shrink-0 text-sm font-medium text-text">
        {qtyText(row.availableQty)}
      </span>
      <span className="shrink-0 text-xs text-text-faint">{unitText(unit)}</span>
    </>
  );

  if (onOrder === undefined) {
    return (
      <li className="flex items-center gap-2 rounded-[var(--radius-sm)] px-1 py-1 text-xs">
        <Warehouse size={13} aria-hidden className="shrink-0 text-text-faint" />
        <span className="min-w-0 break-words text-text-muted">{row.warehouseName}</span>
        <span className="ml-auto flex items-baseline gap-1">{qty}</span>
      </li>
    );
  }

  const empty = row.availableQty <= 0;
  const disabled = ordered || empty || !allowed;
  const short = pending > 0 && free < row.availableQty;

  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        onClick={onOrder}
        title={allowed ? undefined : t('requisition.warehouseForbidden')}
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-[var(--radius-md)] border px-2.5 py-2',
          'text-left transition-[background-color,border-color] duration-[var(--motion-fast)]',
          'ease-[var(--ease-standard)]',
          ordered
            ? 'border-accent bg-accent-soft'
            : disabled
              ? 'border-border bg-surface-2 opacity-60'
              : 'border-border bg-surface hover:border-accent hover:bg-accent-soft',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
            ordered ? 'bg-accent text-[var(--color-accent-contrast)]' : 'text-text-faint',
          )}
        >
          {ordered ? <Check size={14} /> : allowed ? <Warehouse size={14} /> : <Lock size={14} />}
        </span>

        <span className="flex min-w-0 flex-col">
          <span className="min-w-0 break-words text-sm text-text">{row.warehouseName}</span>
          {allowed ? null : (
            <span className="text-xs text-text-faint">{t('requisition.warehouseForbidden')}</span>
          )}
          {/* Kutilayotgan talab — "band" DEMAYDI, chunki u band emas.
              Uni band deb atash yolg'on bo'lardi: hech narsa qulflanmagan
              va talabnoma rad etilsa, son o'z-o'zidan yo'qoladi. */}
          {allowed && short ? (
            <span className={cn('tabular text-xs', free <= 0 ? 'text-danger' : 'text-warn')}>
              {t('stock.pendingDemand', { qty: qtyText(pending), free: qtyText(free) })}
            </span>
          ) : null}
        </span>

        <span className="ml-auto flex shrink-0 items-baseline gap-1">{qty}</span>

        {/* Amalning O'ZI — qatorning nima qilishini aytadi. Belgisiz qator
            bosiladigandek ko'rinmasdi va odam pastdan tugma qidirardi. */}
        <span
          className={cn(
            'flex shrink-0 items-center gap-1 text-xs font-medium',
            ordered ? 'text-accent' : disabled ? 'text-text-faint' : 'text-text-muted',
          )}
        >
          {ordered ? null : <Plus size={13} aria-hidden />}
          {t(ordered ? 'requisition.inBasket' : 'requisition.addToBasket')}
        </span>
      </button>
    </li>
  );
};
